import { useState } from "react";
import {
  Modal,
  Button,
  Form,
  InputGroup,
  Spinner,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import api from "../utils/api";
import { useDispatch } from "react-redux";
import { registerUser } from "../../redux/userSlice";
import "./AuthModal.css";
import { FcGoogle } from "react-icons/fc";

export default function SignUpModal({ show, handleClose }) {
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    referralCode: "",
  });

  const [toast, setToast] = useState({
    show: false,
    message: "",
    variant: "success",
  });

  const [errors, setErrors] = useState({});

  // ===== VALIDATION HELPERS =====
  const validateStep1 = () => {
    const newErrors = {};
    if (!mobile) {
      newErrors.mobile = "Mobile number is required";
    } else if (mobile.length !== 10) {
      newErrors.mobile = "Mobile number must be 10 digits";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!otp) {
      newErrors.otp = "OTP is required";
    } else if (otp.length !== 6) {
      newErrors.otp = "OTP must be 6 digits";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (!formData.username) newErrors.username = "Username is required";
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ===== HANDLERS =====

  // STEP 1 → SEND OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();

    if (!validateStep1()) return;

    setLoading(true);

    try {
      const res = await api.post("/users/send-otp", { mobile });
      console.log("SEND OTP RESPONSE 👉", res.data);

      // 👇 SUCCESS CHECK
      if (res.data?.success) {
        setToast({
          show: true,
          message: "OTP sent successfully 📲",
          variant: "success",
        });
        setStep(2);
      } else {
        throw new Error("OTP not sent");
      }
    } catch (err) {
      console.log("SEND OTP ERROR 👉", err.response?.data || err.message);
      setToast({
        show: true,
        message: err.response?.data?.message || "OTP send failed",
        variant: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  // STEP 2 → VERIFY OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;

    setLoading(true);

    try {
      await api.post("/users/verify-otp", { mobile, otp });

      setToast({
        show: true,
        message: "OTP verified ✅",
        variant: "success",
      });

      setStep(3);
    } catch (err) {
      setToast({
        show: true,
        message: err.response?.data?.message || "Invalid OTP",
        variant: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  // STEP 3 → REGISTER
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateStep3()) return;
    // setLoading(true); // Redux handles loading state

    const payload = {
      username: formData.username,
      password: formData.password,
      mobile,
      referralCode: formData.referralCode,
    };

    const resultAction = await dispatch(registerUser(payload));

    if (registerUser.fulfilled.match(resultAction)) {
      setToast({
        show: true,
        message: "Registration successful 🎉",
        variant: "success",
      });

      setTimeout(() => {
        handleClose();
        setStep(1);
        setMobile("");
        setOtp("");
        setFormData({ username: "", password: "", referralCode: "" });
        setErrors({});
      }, 1200);
    } else {
      setToast({
        show: true,
        message: resultAction.payload || "Registration failed",
        variant: "danger",
      });
    }
    // ensure loading is handled by Redux state or local state if preferred for steps
  };

  // ===== UI =====
  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        centered
        scrollable
        size="lg"
        className="auth-modal"
        backdrop="static"
      >
        <Modal.Body className="p-0">
          <div className="login-container position-relative d-flex flex-column flex-md-row">
            {/* LEFT IMAGE */}
            <div className="login-left d-none d-md-flex w-50">
              <img
                // src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRgH-0j7pDw3YNquJ8HQ6YkIA4iqmVlJRyvVg&s"
                src="/login-register-image.png"
                alt="Banner"
                className="w-100 h-100 object-fit-cover"
              />
            </div>

            {/* RIGHT FORM */}
            <div className="login-right">
              <button
                className="btn-close-custom"
                onClick={handleClose}
                aria-label="Close"
              >
                &times;
              </button>

              <div className="text-center d-flex gap-2 align-items-center mb-2">
                <h4 className="text-white fw-bold">Register with</h4>
                <h4 className="text-orange fw-bold">Ambe Worlds</h4>
              </div>

              {/* <h5 className="login-title">Create Account</h5> */}

              {/* STEP 1 */}
              {step === 1 && (
                <Form onSubmit={handleSendOtp}>
                  <Form.Group className="mb-2">
                    <Form.Label>Mobile Number</Form.Label>
                    <InputGroup hasValidation>
                      <InputGroup.Text className="prefix-text">
                        +91
                      </InputGroup.Text>
                      <Form.Control
                        value={mobile}
                        onChange={(e) => {
                          setMobile(e.target.value);
                          if (errors.mobile)
                            setErrors({ ...errors, mobile: "" });
                        }}
                        maxLength={10}
                        placeholder="Enter Mobile Number"
                        isInvalid={!!errors.mobile}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.mobile}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>

                  <Button
                    type="submit"
                    className="w-100 sign-btn border-0"
                    disabled={loading}
                  >
                    {loading ? <Spinner size="sm" /> : "Send OTP"}
                  </Button>
                </Form>
              )}

              {/* STEP 2 */}
              {step === 2 && (
                <Form onSubmit={handleVerifyOtp}>
                  <Form.Group className="mb-3">
                    <Form.Label>Enter OTP</Form.Label>
                    <Form.Control
                      value={otp}
                      onChange={(e) => {
                        setOtp(e.target.value);
                        if (errors.otp) setErrors({ ...errors, otp: "" });
                      }}
                      placeholder="6 digit OTP"
                      isInvalid={!!errors.otp}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.otp}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Button
                    type="submit"
                    className="w-100 sign-btn"
                    disabled={loading}
                  >
                    {loading ? <Spinner size="sm" /> : "Verify OTP"}
                  </Button>
                </Form>
              )}

              {/* STEP 3 */}
              {step === 3 && (
                <Form onSubmit={handleRegister}>
                  <Form.Group className="mb-3">
                    <Form.Label>Username</Form.Label>
                    <Form.Control
                      value={formData.username}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          username: e.target.value,
                        });
                        if (errors.username)
                          setErrors({ ...errors, username: "" });
                      }}
                      isInvalid={!!errors.username}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.username}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={formData.password}
                      placeholder="Enter Password"
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          password: e.target.value,
                        });
                        if (errors.password)
                          setErrors({ ...errors, password: "" });
                      }}
                      isInvalid={!!errors.password}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.password}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Referral Code (Optional)</Form.Label>
                    <Form.Control
                      value={formData.referralCode}
                      placeholder="Enter Referral Code"
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          referralCode: e.target.value.toUpperCase(),
                        });
                      }}
                    />
                  </Form.Group>

                  <Button
                    type="submit"
                    className="w-100 sign-btn"
                    disabled={loading}
                  >
                    {loading ? <Spinner size="sm" /> : "Register"}
                  </Button>
                </Form>
              )}

              <div className="mt-auto pt-3">
                <div className="divider-container">
                  <div className="divider-line"></div>
                  <div className="divider-text">OR</div>
                  <div className="divider-line"></div>
                </div>

                <button className="google-btn">
                  <FcGoogle size={22} />
                  <span>Continue with Google</span>
                </button>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* TOAST */}
      <ToastContainer position="top-end" className="p-3">
        <Toast
          bg={toast.variant}
          show={toast.show}
          delay={3000}
          autohide
          onClose={() => setToast({ ...toast, show: false })}
        >
          <Toast.Body className="text-white fw-bold">
            {toast.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
}
